import { CamerasAPI, RecordsAPI, VideosAPI } from "./api";
import { apiErrorMessage } from '../utils/apiError';

export interface ChunkedUploadOptions {
    chunkSize?: number;
    maxConcurrent?: number;
    onProgress?: (progress: number) => void;
    onChunkProgress?: (chunkIndex: number, progress: number) => void;
    onChunkComplete?: (chunkIndex: number) => void;
    onChunkError?: (chunkIndex: number, error: Error) => void;
}

export interface ChunkedUploadResult {
    success: boolean;
    blob_name?: string;
    error?: string;
}

interface UploadHandlers {
    requestUploadUrl(): Promise<{ upload_url: string; blob_name: string }>;
    confirmUpload(finalBlobName: string): Promise<void>;
}

async function uploadFileInChunks(
    handlers: UploadHandlers,
    file: File,
    options: ChunkedUploadOptions = {},
): Promise<ChunkedUploadResult> {
    const chunkSize = options.chunkSize || 5 * 1024 * 1024;
    const maxConcurrent = options.maxConcurrent || 3;
    const onProgress = options.onProgress || (() => { });
    const onChunkProgress = options.onChunkProgress || (() => { });
    const onChunkComplete = options.onChunkComplete || (() => { });
    const onChunkError = options.onChunkError || (() => { });

    try {
        const { upload_url, blob_name } = await handlers.requestUploadUrl();

        const url = new URL(upload_url);
        const sasToken = url.search.substring(1);
        const baseUrl = upload_url.split('?')[0];

        const fileSize = file.size;
        const numChunks = Math.ceil(fileSize / chunkSize);
        const blockIds: string[] = [];

        for (let i = 0; i < numChunks; i++) {
            blockIds.push(btoa(`block-${i.toString().padStart(8, '0')}`));
        }

        let completedChunks = 0;
        let failedChunks = 0;
        let activeUploads = 0;
        let chunkIndex = 0;

        const uploadChunk = async (index: number): Promise<void> => {
            const start = index * chunkSize;
            const end = Math.min(start + chunkSize, fileSize);
            const chunk = file.slice(start, end);
            const blockId = blockIds[index];

            try {
                const blockUrl = `${baseUrl}?comp=block&blockid=${encodeURIComponent(blockId)}&${sasToken}`;

                await new Promise<void>((resolve, reject) => {
                    const xhr = new XMLHttpRequest();

                    xhr.upload.addEventListener('progress', (e) => {
                        if (e.lengthComputable) {
                            const chunkProgress = Math.round((e.loaded / e.total) * 100);
                            onChunkProgress(index, chunkProgress);
                            const overallProgress = Math.round(
                                ((completedChunks * chunkSize) + (e.loaded * (end - start) / e.total)) / fileSize * 100
                            );
                            onProgress(overallProgress);
                        }
                    });

                    xhr.addEventListener('load', () => {
                        if (xhr.status >= 200 && xhr.status < 300) resolve();
                        else reject(new Error(`Block upload failed with status ${xhr.status}`));
                    });
                    xhr.addEventListener('error', () => reject(new Error('Block upload failed')));

                    xhr.open('PUT', blockUrl);
                    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
                    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
                    xhr.send(chunk);
                });

                completedChunks++;
                onChunkComplete(index);
            } catch (error) {
                failedChunks++;
                onChunkError(index, error instanceof Error ? error : new Error('Unknown error'));
                throw error;
            } finally {
                activeUploads--;
            }
        };

        return new Promise<ChunkedUploadResult>((resolve) => {
            const processQueue = async () => {
                if (chunkIndex >= numChunks) {
                    if (activeUploads === 0) {
                        if (failedChunks > 0) {
                            resolve({ success: false, error: `${failedChunks} chunks failed to upload` });
                            return;
                        }

                        try {
                            const commitUrl = `${baseUrl}?comp=blocklist&${sasToken}`;
                            const blockListXml = `<?xml version="1.0" encoding="utf-8"?>
                <BlockList>
                  ${blockIds.map(id => `<Latest>${id}</Latest>`).join('')}
                </BlockList>`;

                            await new Promise<void>((resolveCommit, rejectCommit) => {
                                const xhr = new XMLHttpRequest();
                                xhr.addEventListener('load', () => {
                                    if (xhr.status >= 200 && xhr.status < 300) resolveCommit();
                                    else rejectCommit(new Error(`Commit failed with status ${xhr.status}`));
                                });
                                xhr.addEventListener('error', () => rejectCommit(new Error('Commit failed')));
                                xhr.open('PUT', commitUrl);
                                xhr.setRequestHeader('Content-Type', 'application/xml');
                                xhr.send(blockListXml);
                            });

                            let finalBlobName = blob_name;
                            const fileExtension = file.name.split('.').pop()?.toLowerCase();
                            if (fileExtension && !finalBlobName.endsWith(`.${fileExtension}`)) {
                                if (finalBlobName.includes('.')) {
                                    finalBlobName = finalBlobName.substring(0, finalBlobName.lastIndexOf('.'));
                                }
                                finalBlobName = `${finalBlobName}.${fileExtension}`;
                            }

                            await handlers.confirmUpload(finalBlobName);
                            resolve({ success: true, blob_name: finalBlobName });
                        } catch (error) {
                            resolve({
                                success: false,
                                  error: apiErrorMessage(error, 'Upload confirmation failed'),
                            });
                        }
                    }
                    return;
                }

                if (activeUploads < maxConcurrent && chunkIndex < numChunks) {
                    const currentChunkIndex = chunkIndex++;
                    activeUploads++;

                    uploadChunk(currentChunkIndex)
                        .catch(() => { })
                        .finally(() => { processQueue(); });

                    processQueue();
                }
            };

            processQueue();
        });
    } catch (error) {
          return { success: false, error: apiErrorMessage(error, 'Upload failed') };
    }
}

export const BigFilesAPI = {
    uploadChunked: async (
        title: string,
        file: File,
        hash_id: string,
        cameraId: string,
        options: ChunkedUploadOptions = {},
        recordId?: number,
        cameraModel?: string,
        cameraMode?: string,
    ): Promise<ChunkedUploadResult> => {
        const handlers: UploadHandlers = recordId ? {
            requestUploadUrl: () => RecordsAPI.requestUpload(recordId, {
                title,
                content_type: file.type || "application/octet-stream",
                type: "video",
                hash_id,
                file_size: file.size,
                camera_id: cameraId,
                camera_model: cameraModel || "unknown",
                camera_mode: cameraMode || "unknown",
            }).then(r => r.data),
            confirmUpload: () => RecordsAPI.confirmUpload(recordId, { hash_id }).then(() => {}),
        } : {
            requestUploadUrl: () => CamerasAPI.requestCalibrationVideoUpload(cameraId, {
                title,
                content_type: file.type || "application/octet-stream",
            }).then(r => r.data),
            confirmUpload: (finalBlobName) => {
                const fileDateTime = new Date(file.lastModified).toISOString();
                return CamerasAPI.confirmCalibrationVideoUpload(cameraId, {
                    blob_name: finalBlobName.toLowerCase(),
                    title: title.toLowerCase(),
                    content_type: file.type || "application/octet-stream",
                    date_time: fileDateTime,
                    file_size: file.size,
                    camera_model: cameraModel,
                    camera_mode: cameraMode,
                }).then(() => {});
            },
        };
        return uploadFileInChunks(handlers, file, options);
    },

    uploadChunkedPreCreated: async (
        recordId: number,
        hashId: string,
        uploadUrl: string,
        blobName: string,
        file: File,
        options: ChunkedUploadOptions = {},
    ): Promise<ChunkedUploadResult> => {
        // The record and its video were already created (with a SAS upload URL) by
        // batch_create_records, so we skip request_upload and upload straight to the URL.
        const handlers: UploadHandlers = {
            requestUploadUrl: () => Promise.resolve({ upload_url: uploadUrl, blob_name: blobName }),
            confirmUpload: () => RecordsAPI.confirmUpload(recordId, { hash_id: hashId }).then(() => {}),
        };
        return uploadFileInChunks(handlers, file, options);
    },

    uploadChunkedReupload: async (
        videoId: number,
        file: File,
        options: ChunkedUploadOptions = {},
    ): Promise<ChunkedUploadResult> => {
        const handlers: UploadHandlers = {
            requestUploadUrl: () => VideosAPI.requestReupload(videoId).then(r => r.data),
            confirmUpload: () => VideosAPI.confirmReupload(videoId).then(() => {}),
        };
        return uploadFileInChunks(handlers, file, options);
    },
};
